import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Box,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import { StyledButton } from "components/StyledComponents/styledComponents";

export default function ReceivedBusinessBreakdownDialog({
  open,
  onClose,
  startDate,
  endDate,
  dsaId,
  user,
  setAlert,
  urlApi,
  selectedDsaId,
  dialogType,
}) {
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [filters, setFilters] = useState([{ column: null, text: "" }]);
  const [columnOptions, setColumnOptions] = useState([]);

  const jwt_token = Cookie.get("jwt_token");
  const showDsaColumn = selectedDsaId !== 0;

  const makeNumeric = (headerName, field) => ({
    headerName,
    field,
    sortable: true,
    filter: true,
    type: "numericColumn",
    aggFunc: "sum",

    cellStyle: (params) => ({
      fontWeight: params.node?.rowPinned === "bottom" ? "bold" : "500",
      textAlign: "right",
      whiteSpace: "normal",
      lineHeight: "1.4",
      paddingTop: "6px",
      paddingBottom: "6px",
    }),
    valueFormatter: (params) =>
      params.value == null
        ? ""
        : Number(params.value).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
  });

  const columnDefs = useMemo(() => {
    const cols = [
      {
        headerName: "Application Number",
        field: "applicationNumber",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Business Type",
        field: "businessType",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Bank Name",
        field: "bankerName",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Customer Name",
        field: "customerName",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Provider Name",
        field: "providerName",
        sortable: true,
        filter: true,
      },
    ];

    if (showDsaColumn) {
      cols.push({
        headerName: "DSA Name",
        field: "dsaName",
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.dsaName || "",
      });
    }

    cols.push(
      makeNumeric("Disbursed Amount", "disbursedAmount"),
      makeNumeric("DMA Payout", "dmaPayout"),
      makeNumeric("Revenue", "amount")
    );

    return cols;
  }, [showDsaColumn]);

  useEffect(() => {
    const options = columnDefs
      .filter((col) => col.field)
      .map((col) => ({ label: col.headerName, value: col.field, field: col.field }));
    setColumnOptions(options);
    setFilters([{ column: null, text: "" }]);
  }, [columnDefs]);

  const activeFilters = filters.filter((f) => f.column && f.text.trim() !== "");

  useEffect(() => {
    if (activeFilters.length === 0) {
      setFilteredRowData(rowData);
      return;
    }
    const filtered = rowData.filter((row) =>
      activeFilters.every(({ column, text }) => {
        const val = row[column.field];
        if (val == null) return false;
        if (typeof val === "number") return val.toString().includes(text);
        return val.toString().toLowerCase().includes(text.toLowerCase());
      })
    );
    setFilteredRowData(filtered);
  }, [rowData, filters]);

  const calculateFooterTotals = useCallback((data) => {
    if (!data || data.length === 0) return { disbursedAmount: 0, dmaPayout: 0, amount: 0 };
    return {
      disbursedAmount: data.reduce((sum, row) => sum + (row.disbursedAmount || 0), 0),
      dmaPayout: data.reduce((sum, row) => sum + (row.dmaPayout || 0), 0),
      amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
    };
  }, []);

  useEffect(() => {
    const dataToUse = activeFilters.length > 0 ? filteredRowData : rowData;
    const totals = calculateFooterTotals(dataToUse);
    setPinnedBottomRowData([
      {
        applicationNumber: "",
        businessType: "Total",
        bankerName: "",
        customerName: "",
        providerName: "",
        dsaName: "",
        disbursedAmount: totals.disbursedAmount,
        dmaPayout: totals.dmaPayout,
        amount: totals.amount,
      },
    ]);
  }, [rowData, filteredRowData, filters, calculateFooterTotals]);

  const handleColumnChange = (index, newColumn) => {
    setFilters((prev) => {
      const updated = [...prev];
      updated[index] = { column: newColumn, text: "" };
      return updated;
    });
  };

  const handleTextChange = (index, value) => {
    setFilters((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text: value };
      return updated;
    });
  };

  const handleAddFilter = () => setFilters((prev) => [...prev, { column: null, text: "" }]);
  const handleClearAll = () => setFilters([{ column: null, text: "" }]);
  const handleRemoveFilter = (index) =>
    setFilters((prev) =>
      prev.length === 1 ? [{ column: null, text: "" }] : prev.filter((_, i) => i !== index)
    );

  const usedFields = filters.map((f) => f.column?.field).filter(Boolean);

  const handleDownloadExcel = () => {
    let dataToExport = [];
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        if (node.data) dataToExport.push(node.data);
      });
    } else {
      dataToExport = filteredRowData;
    }
    if (!dataToExport || dataToExport.length === 0) return;

    const totals = calculateFooterTotals(dataToExport);
    const totalRow = {
      applicationNumber: "",
      businessType: "Total",
      bankerName: "",
      customerName: "",
      providerName: "",
      dsaName: "",
      disbursedAmount: totals.disbursedAmount,
      dmaPayout: totals.dmaPayout,
      amount: totals.amount,
    };

    const allData = [...dataToExport, totalRow];
    const exportData = allData.map((row) => ({
      "Application Number": row.applicationNumber || "",
      "Business Type": row.businessType || "",
      "Bank Name": row.bankerName || "",
      "Customer Name": row.customerName || "",
      "Provider Name": row.providerName || "",
      ...(showDsaColumn ? { "DSA Name": row.dsaName || "" } : {}),
      "Disbursed Amount": row.disbursedAmount || 0,
      "DMA Payout": row.dmaPayout || 0,
      Revenue: row.amount || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Business Breakdown");

    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${
        dialogType === "received" ? "Received" : "NotReceived"
      }_Breakdown_${startDate}_to_${endDate}.xlsx`
    );
  };

  useEffect(() => {
    if (!open) {
      setRowData([]);
      setFilteredRowData([]);
      setFilters([{ column: null, text: "" }]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      if (!user) {
        setAlert("Please login again", "error");
        setLoading(false);
        return;
      }

      const url = (() => {
        if (dialogType === "received" || dialogType === "notReceived") {
          return selectedDsaId === 0
            ? `${urlApi}?fromDate=${startDate}&toDate=${endDate}&assignCompanyId=${selectedDsaId}`
            : `${urlApi}?fromDate=${startDate}&toDate=${endDate}&companyId=${selectedDsaId}`;
        } else {
          return selectedDsaId === 0
            ? `${urlApi}?startDate=${startDate}&endDate=${endDate}`
            : `${urlApi}?startDate=${startDate}&endDate=${endDate}&dsaId=${selectedDsaId}`;
        }
      })();

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        });
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        const processedData = data.map((row) => ({ ...row, dsaName: row.dsaName || "" }));
        setRowData(processedData);
        setFilteredRowData(processedData);
        if (gridRef.current?.api) {
          setTimeout(() => gridRef.current.api.sizeColumnsToFit(), 0);
        }
      } catch (error) {
        console.error("Error fetching breakdown data:", error);
        setAlert("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, startDate, endDate, dsaId, jwt_token, user, urlApi, selectedDsaId, setAlert]);

  const displayData = activeFilters.length > 0 ? filteredRowData : rowData;

  const dialogTitle =
    dialogType === "received"
      ? "Received Revenue Breakdown"
      : dialogType === "notReceived"
      ? "Not Received Revenue Breakdown"
      : "Business Breakdown";

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f4f6f8",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Top Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={700} color="#1F2937">
          {dialogTitle}
        </Typography>

        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          disabled={rowData.length === 0}
          sx={{ textTransform: "none" }}
        >
          Download Excel
        </StyledButton>
      </Box>

      {/* ── Filter Section ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#333">
              🔍 Filters
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleAddFilter}
                disabled={filters.length >= columnOptions.length}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                + Add Filter
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleClearAll}
                disabled={filters.length === 1 && !filters[0].column && !filters[0].text}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>

          {filters.map((filter, index) => (
            <Grid
              container
              spacing={1.5}
              alignItems="center"
              key={index}
              sx={{ mb: index < filters.length - 1 ? 1.2 : 0 }}
            >
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={columnOptions.filter(
                    (opt) => !usedFields.includes(opt.field) || opt.field === filter.column?.field
                  )}
                  getOptionLabel={(option) => option.label}
                  value={filter.column}
                  onChange={(_, newVal) => handleColumnChange(index, newVal)}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Column ${index + 1}`}
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label={filter.column ? `Search in "${filter.column.label}"` : "Search value"}
                  variant="outlined"
                  size="small"
                  value={filter.text}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  disabled={!filter.column}
                  placeholder={
                    filter.column
                      ? `Type to filter ${filter.column.label}...`
                      : "Select a column first"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFilter(index)}
                  title="Remove filter"
                  sx={{ color: "#d32f2f" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: "block" }}>
            {activeFilters.length > 0 ? (
              <>
                Showing <strong>{filteredRowData.length}</strong> of{" "}
                <strong>{rowData.length}</strong> rows &nbsp;|&nbsp;
                <span style={{ color: "#1976d2" }}>
                  {activeFilters.map((f) => `${f.column.label} = "${f.text}"`).join("  AND  ")}
                </span>
              </>
            ) : (
              <>
                Total <strong>{rowData.length}</strong> rows
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* ── AG Grid — minHeight:0 chain fixes sticky header ── */}
      <Box
        sx={{
          flex: 1,
          px: 3,
          pb: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{
              flex: 1,
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={displayData}
              columnDefs={columnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                flex: 1,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  whiteSpace: "normal",
                  lineHeight: "1.4",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                },
              }}
              onGridReady={(params) => {
                params.api.sizeColumnsToFit();
              }}
              onFirstDataRendered={(params) => {
                params.api.sizeColumnsToFit();
              }}
              animateRows
              pagination
              paginationPageSize={100}
              enableRangeSelection
              suppressAggFuncInHeader
              suppressMovableColumns
              suppressColumnVirtualisation
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold", backgroundColor: "#f5f6f8" };
                }
                return null;
              }}
              statusBar={{
                statusPanels: [
                  { statusPanel: "agTotalRowCountComponent", align: "left" },
                  { statusPanel: "agAggregationComponent", align: "right" },
                ],
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

ReceivedBusinessBreakdownDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  dsaId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
  setAlert: PropTypes.func.isRequired,
  urlApi: PropTypes.string.isRequired,
  selectedDsaId: PropTypes.number,
  dialogType: PropTypes.oneOf(["received", "notReceived", "business"]).isRequired,
};
